import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import {
  STORAGE_READ_FACADE_TOKEN,
  type IStorageReadFacade,
} from "../../api/storage";
import { ViewModel } from "../../lib/vvm";
import { Box } from "../../stores/Box";
import { Product } from "../../stores/Product";
import { STORE_TOKEN, type IStore } from "../../stores/store";
import {
  NAVIGATION_STORE_TOKEN,
  type INavigationStore,
  Screens,
} from "../../stores/navigation";
import { BOX_ID_PATTERN, PRODUCT_ID_PATTERN } from "../../domain/entity-ids";
import { formatTypeTag, normalizeTypeTag } from "../../utils/typeTags";

export interface BoxesListPageViewProps {
  title?: string;
  q?: string;
  typeTag?: string;
}

@injectable()
export class BoxesListPageViewModel extends ViewModel<
  unknown,
  BoxesListPageViewProps
> {
  private readonly pageSize = 30;
  private activeRequestKey = "";

  @observable.shallow
  items: Box[] = [];

  @observable
  total = 0;

  @observable
  offset = 0;

  @observable
  isLoadingInitial = false;

  @observable
  isLoadingMore = false;

  @observable
  hasMore = true;

  @observable
  error: string | null = null;

  constructor(
    @inject(STORAGE_READ_FACADE_TOKEN)
    private readonly storageReadFacade: IStorageReadFacade,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();
    makeObservable(this);
  }

  @computed
  get pageTitle(): string {
    if (this.typeTagFilter) {
      return `Коробки по тегу ${formatTypeTag(this.typeTagFilter)}`;
    }

    return this.viewProps.title?.trim() || "Список коробок";
  }

  @computed
  get summaryLabel(): string {
    if (this.total <= 0) {
      return `${this.items.length} позиций`;
    }

    return `${this.items.length} из ${this.total}`;
  }

  @computed
  get isBusy(): boolean {
    return this.isLoadingInitial || this.isLoadingMore;
  }

  @computed
  get canAutoLoadMore(): boolean {
    return this.hasMore && !this.error;
  }

  @computed
  get queryFilter(): string {
    return this.viewProps.q?.trim() || "";
  }

  @computed
  get typeTagFilter(): string {
    return normalizeTypeTag(this.viewProps.typeTag || "") || "";
  }

  @computed
  private get requestKey(): string {
    return [this.queryFilter, this.typeTagFilter].join("|");
  }

  protected onViewMounted(): void {
    this.activeRequestKey = this.requestKey;
    void this.refresh();
  }

  protected onViewUpdated(): void {
    if (this.requestKey === this.activeRequestKey) {
      return;
    }

    this.activeRequestKey = this.requestKey;
    void this.refresh();
  }

  @action.bound
  async refresh(): Promise<void> {
    runInAction(() => {
      this.items = [];
      this.total = 0;
      this.offset = 0;
      this.hasMore = true;
      this.error = null;
    });

    await this.loadMore();
  }

  @action.bound
  async loadMore(): Promise<void> {
    if (!this.hasMore || this.isBusy) {
      return;
    }

    const isInitial = this.items.length === 0;

    runInAction(() => {
      this.error = null;
      if (isInitial) {
        this.isLoadingInitial = true;
      } else {
        this.isLoadingMore = true;
      }
    });

    try {
      const page = await this.storageReadFacade.getBoxesPage({
        limit: this.pageSize,
        offset: this.offset,
        q: this.queryFilter,
        typeTag: this.typeTagFilter || undefined,
      });
      const loadedItems = page.items.map((item) => new Box(item));

      runInAction(() => {
        this.items = this.mergeItems(this.items, loadedItems);
        this.total = page.total;
        this.offset = page.offset + page.items.length;
        this.hasMore = page.items.length > 0 && this.items.length < page.total;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить список коробок";
      });
    } finally {
      runInAction(() => {
        this.isLoadingInitial = false;
        this.isLoadingMore = false;
      });
    }
  }

  @action.bound
  openBox(box: Box): void {
    const existing = this.store.boxes.find((item) => item.id === box.id);

    if (existing) {
      this.navigationStore.setScreen(Screens.Box, existing);
      return;
    }

    this.store.addBox(box);
    this.navigationStore.setScreen(Screens.Box, box);
  }

  resolveParentLabel = (box: Box): string => {
    const parentId = box.parentId?.trim() || "";
    if (!parentId) {
      return "Верхний уровень";
    }

    const fromLoaded =
      this.items.find((item) => item.id === parentId)?.name ||
      this.store.boxes.find((item) => item.id === parentId)?.name;

    if (fromLoaded?.trim()) {
      return `Внутри: ${fromLoaded}`;
    }

    return `Родитель: ${parentId}`;
  };

  @action.bound
  openTypeTag(tag: string): void {
    const normalizedTag = normalizeTypeTag(tag || "");
    if (!normalizedTag) {
      return;
    }

    this.navigationStore.setScreen(Screens.BoxesList, {
      title: `Коробки по тегу ${formatTypeTag(normalizedTag)}`,
      typeTag: normalizedTag,
    });
  }

  @action.bound
  openParentReference(box: Box): void {
    const parentId = box.parentId?.trim() || "";
    if (!BOX_ID_PATTERN.test(parentId)) {
      this.navigationStore.setScreen(Screens.BoxesList, {
        title: "Список коробок",
      });
      return;
    }

    void this.openBoxById(parentId);
  }

  @action.bound
  openReferenceId(id: string): void {
    const normalizedId = id.trim();
    if (!normalizedId) {
      return;
    }

    if (BOX_ID_PATTERN.test(normalizedId)) {
      const existingBox = this.store.boxes.find(
        (item) => item.id === normalizedId,
      );
      if (existingBox) {
        this.navigationStore.setScreen(Screens.Box, existingBox);
        return;
      }

      void this.openBoxById(normalizedId);
      return;
    }

    if (PRODUCT_ID_PATTERN.test(normalizedId)) {
      const existingProduct = this.store.products.find(
        (item) => item.id === normalizedId,
      );
      if (existingProduct) {
        this.navigationStore.setScreen(Screens.Product, existingProduct);
        return;
      }

      void this.openProductById(normalizedId);
    }
  }

  private mergeItems(current: Box[], next: Box[]): Box[] {
    const merged = new Map<string, Box>();

    current.forEach((item) => {
      merged.set(item.id, item);
    });

    next.forEach((item) => {
      merged.set(item.id, item);
    });

    return Array.from(merged.values());
  }

  private async openBoxById(boxId: string): Promise<void> {
    try {
      const box = await this.storageReadFacade.getBoxById(boxId);
      if (!box) {
        runInAction(() => {
          this.error = "Коробка не найдена";
        });
        return;
      }

      const model = new Box(box);
      this.store.addBox(model);
      this.navigationStore.setScreen(Screens.Box, model);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Не удалось открыть коробку";
      });
    }
  }

  private async openProductById(productId: string): Promise<void> {
    try {
      const product = await this.storageReadFacade.getProductById(productId);
      if (!product) {
        runInAction(() => {
          this.error = "Товар не найден";
        });
        return;
      }

      const model = new Product(product);
      this.store.addProduct(model);
      this.navigationStore.setScreen(Screens.Product, model);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Не удалось открыть товар";
      });
    }
  }
}
